// qs-cursorManager.ts
const BASE_DRAWING_CURSOR = 'crosshair'

const createDrawingCursor = (brushSize: number): string => {
  const size = Math.max(brushSize, 4)
  const center = size / 2

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${center}" cy="${center}" r="${center}" 
              fill="#000" opacity="0.5"/>
    </svg>
  `
  
  const encodedSvg = encodeURIComponent(svg)
  const cursor = `url("data:image/svg+xml,${encodedSvg}") ${center} ${center}, ${BASE_DRAWING_CURSOR}`
  
  return cursor
}

const createDeleteCursor = (): string => {
  const size = 24
  const center = size / 2

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <line x1="4" y1="4" x2="20" y2="20" stroke="#dc2626" stroke-width="3" stroke-linecap="round"/>
      <line x1="20" y1="4" x2="4" y2="20" stroke="#dc2626" stroke-width="3" stroke-linecap="round"/>
    </svg>
  `
  
  const encodedSvg = encodeURIComponent(svg)
  const cursor = `url("data:image/svg+xml,${encodedSvg}") ${center} ${center}, crosshair`
  
  return cursor
}

const MODE_CURSORS = {
  'drawing': BASE_DRAWING_CURSOR,
  'delete': 'crosshair',
  'pan': 'grab',
  'panning': 'grabbing',
  'position': 'move',
  'text': 'text'
} as const

type CursorType = keyof typeof MODE_CURSORS | string

class CursorManager {
  private currentCursor: string = BASE_DRAWING_CURSOR
  private currentBrushSize: number = 10
  private isInitialized: boolean = false

  init(initialBrushSize?: number): void {
    if (this.isInitialized) return

    if (initialBrushSize) {
      this.currentBrushSize = initialBrushSize
    }
    
    this.setCursor('drawing', this.currentBrushSize)
    this.isInitialized = true
  }

  setCursor(mode: CursorType, brushSize?: number): void {
    let cursor: string
    
    if (mode === 'drawing' && brushSize !== undefined) {
      cursor = createDrawingCursor(brushSize)
      this.currentBrushSize = brushSize
    } else if (mode === 'delete') {
      cursor = createDeleteCursor()
    } else {
      cursor = MODE_CURSORS[mode as keyof typeof MODE_CURSORS] || mode
    }

    document.body.style.cursor = cursor
    this.currentCursor = cursor
  }

  setCustomCursor(cursor: string): void {
    document.body.style.cursor = cursor
    this.currentCursor = cursor
  }

  resetToDrawing(): void {
    this.setCursor('drawing', this.currentBrushSize)
  }

  updateBrushSize(brushSize: number): void {
    this.currentBrushSize = brushSize

    if (this.currentCursor.includes('url(') && !this.currentCursor.includes('delete')) {
      this.setCursor('drawing', brushSize)
    }
  }

  getCurrentCursor(): string {
    return this.currentCursor
  }

  isDrawingCursor(): boolean {
    return this.currentCursor === BASE_DRAWING_CURSOR
  }

  refreshDrawingCursor(): void {
    if (this.isInitialized) {
      this.setCursor('drawing', this.currentBrushSize)
    }
  }

  setTextCursor(): void {
    this.setCursor('text')
  }

  isInputFocused(): boolean {
    const activeElement = document.activeElement
    if (!activeElement) return false
    
    const tagName = activeElement.tagName
    if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
      return true
    }
    
    if ((activeElement as HTMLElement)?.contentEditable === 'true') {
      return true
    }
    
    return false
  }

  isTextEditing(): boolean {
    const activeElement = document.activeElement
    
    if (activeElement?.tagName === 'INPUT') {
      const inputElement = activeElement as HTMLInputElement
      const style = window.getComputedStyle(inputElement)
      if (style.position === 'absolute' && parseInt(style.zIndex) >= 1000) {
        return true
      }
    }
    
    try {
      const textStore = (window as any).__vectorsketch_text_store
      if (textStore && textStore.isEditingText) {
        return textStore.isEditingText.value === true
      }
    } catch (error) {
    }
    
    const textInputs = document.querySelectorAll('input[style*="position: absolute"]')
    if (textInputs.length > 0) {
      for (let i = 0; i < textInputs.length; i++) {
        const input = textInputs[i] as HTMLInputElement
        const style = window.getComputedStyle(input)
        if (parseInt(style.zIndex) >= 1000) {
          return true
        }
      }
    }
    
    return false
  }

  isAnyInputActive(): boolean {
    if (this.isInputFocused()) {
      return true
    }
    
    if (this.isTextEditing()) {
      return true
    }
    
    return false
  }
}

export const cursorManager = new CursorManager()

cursorManager.init()